import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Icon } from '@/components/synapse/Icon';
import { translateApiError } from '@/lib/apiError';
import type { PatientSex } from '@/types/patients';
import { VisitSummaryPanel } from '@/features/patients/VisitSummaryPanel';
import { formatShortDate } from '@/features/patients/patientDisplay';
import { useConsultation, usePatientConsultations } from '@/features/patients/useConsultations';
import { visitPreview } from '@/features/patients/visitPreview';
import { formatVisitRecency } from '@/features/patients/visitRecency';

interface VisitHistoryListProps {
  patientId: string;
  sex: PatientSex;
}

/**
 * The patient's past visits, newest first, each opening in place to show its
 * diagnosis and medicines.
 *
 * In place rather than on its own page: the doctor is mid-consultation with
 * this patient, and a tap that navigates away loses the number and the patient
 * header they are working from. One visit is open at a time.
 */
export function VisitHistoryList({ patientId, sex }: VisitHistoryListProps) {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = usePatientConsultations(patientId);
  const [openId, setOpenId] = useState<string | null>(null);

  if (isLoading) {
    return <p className="px-4 py-6 text-[13px] text-muted">{t('common.loading')}</p>;
  }
  if (isError) {
    return (
      <p role="alert" className="px-4 py-6 text-[13px] text-destructive">
        {translateApiError(error, { fallback: t('patients.couldNotLoadVisits') })}
      </p>
    );
  }

  const visits = [...(data ?? [])].sort((a, b) => b.visitDate.getTime() - a.visitDate.getTime());
  if (visits.length === 0) {
    return (
      <div className="px-4 py-8 text-center">
        <div className="text-[13.5px] font-semibold tracking-[-0.01em]">{t('patients.noVisitsYet')}</div>
        <p className="mt-1 text-[12.5px] text-muted">{t('consulting.firstVisit')}</p>
      </div>
    );
  }

  return (
    <ul className="divide-y divide-line">
      {visits.map((visit) => {
        const open = openId === visit.id;
        const panelId = `visit-${visit.id}`;
        return (
          <li key={visit.id}>
            <button
              type="button"
              onClick={() => setOpenId(open ? null : visit.id)}
              aria-expanded={open}
              aria-controls={panelId}
              className="flex w-full items-center gap-3 px-4 py-3.5 text-left hover:bg-paper-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
            >
              <span className="flex w-[84px] shrink-0 flex-col gap-0.5 text-muted">
                <span className="font-mono text-[12.5px] font-semibold leading-tight">
                  {formatShortDate(visit.visitDate)}
                </span>
                <span className="text-[11px] leading-tight">{formatVisitRecency(visit.visitDate, t)}</span>
              </span>
              <span className="min-w-0 flex-1 truncate text-[14px] font-semibold tracking-[-0.01em]">
                {visitPreview(visit.payload) ?? t('patients.visitNoDiagnosis')}
              </span>
              {visit.status === 'draft' && (
                <Badge tone="warn" size="sm">
                  {t('patients.statusDraft')}
                </Badge>
              )}
              <Icon name={open ? 'chevron_up' : 'chevron_down'} size={16} className="text-muted" />
            </button>
            {open && (
              <div id={panelId} className="border-t border-line bg-background px-4 py-4">
                <VisitDetail consultationId={visit.id} sex={sex} />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * One visit, opened. Fetched on open rather than taken from the list: the list
 * carries no dispensed medicines, and opening a visit is the PHI read the
 * server logs.
 */
function VisitDetail({ consultationId, sex }: { consultationId: string; sex: PatientSex }) {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useConsultation(consultationId);
  if (isLoading) return <p className="text-[13px] text-muted">{t('common.loading')}</p>;
  if (isError || !data) {
    return (
      <p role="alert" className="text-[13px] text-destructive">
        {translateApiError(error, { fallback: t('consult.couldNotLoad') })}
      </p>
    );
  }
  return (
    <VisitSummaryPanel
      payload={data.payload}
      sex={sex}
      dispensed={data.dispensed}
      recordedFromPaper={data.clinicianUserId === null}
    />
  );
}
